import type * as React from "react";

/** Circular entity orb — photo, initials, or icon, tinted by entity tone. */
export interface AvatarProps {
  /** Image URL. Takes precedence over icon/initials. */
  src?: string;
  alt?: string;
  /** Fallback initials when no src/icon. */
  initials?: string;
  /** Icon node shown when no src. */
  icon?: React.ReactNode;
  /** @default "person" */
  tone?: "person" | "company" | "event" | "opportunity" | "note";
  /** @default "md" */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Soft entity glow ring — reads as a graph node. @default false */
  glow?: boolean;
  style?: React.CSSProperties;
}

const TONES: Record<NonNullable<AvatarProps["tone"]>, string> = {
  person: "var(--entity-person)",
  company: "var(--entity-company)",
  event: "var(--entity-event)",
  opportunity: "var(--entity-opportunity)",
  note: "var(--entity-note)",
};

const DIMS: Record<NonNullable<AvatarProps["size"]>, number> = { xs: 24, sm: 32, md: 44, lg: 56, xl: 72 };

const GLOW_RINGS: Record<NonNullable<AvatarProps["tone"]>, string> = {
  person: "0 0 0 5px rgba(74,140,176,0.14)",
  company: "0 0 0 5px rgba(107,168,114,0.15)",
  event: "0 0 0 5px rgba(217,151,79,0.16)",
  opportunity: "0 0 0 5px rgba(203,169,79,0.16)",
  note: "0 0 0 5px rgba(131,122,106,0.14)",
};

export function Avatar({ src, alt, initials, icon, tone = "person", size = "md", glow = false, style }: AvatarProps) {
  const d = DIMS[size];
  const color = TONES[tone];
  const glowRing = GLOW_RINGS[tone];

  return (
    <div
      style={
        {
          width: `${d}px`,
          height: `${d}px`,
          flex: "none",
          borderRadius: "var(--radius-pill)",
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
          background: src ? "var(--surface-sunken)" : color,
          color: "var(--text-strong)",
          fontFamily: "var(--font-sans)",
          fontWeight: "var(--fw-bold)",
          fontSize: `${Math.round(d * 0.38)}px`,
          boxShadow: glow ? `${glowRing}, var(--shadow-sm)` : "var(--shadow-xs)",
          border: src ? "1px solid var(--border-subtle)" : "none",
          ...style,
        } as React.CSSProperties
      }
    >
      {src ? (
        <img src={src} alt={alt || ""} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : icon ? (
        <span style={{ display: "grid", placeItems: "center" }}>{icon}</span>
      ) : (
        initials
      )}
    </div>
  );
}
